import { DuplicateButton } from "@components/buttons/duplicate";
import { GrayDeleteButton } from "@components/buttons/gray-delete";
import { RollBackButton } from "@components/buttons/rollback";
import {
  SHBox,
  SHButton,
  SHDataGrid,
  SHDataGridRef,
  SHStack,
} from "@components/design-systems";
import { DefaultDataState } from "@components/design-systems/sh-data-grid/constant";
import { EditSVG } from "@components/svgs";
import { PageRoutes } from "@constants";
import { useNotification } from "@hooks/useNotification";
import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import { useUserPermissions } from "@hooks/userUserPermission";
import { TopBar, TopBarContainer } from "@layouts/top-bar";
import { PageMode, ParentState } from "@models/core";
import { AnalysisListDTO } from "@models/platform-analysis/entities/analysis";
import { AnalysisStatus } from "@models/platform-analysis/enums/status";
import { useTheme } from "@mui/material";
import { AddNewAnalysisButton } from "@pages/platform-analysis-v2/components/buttons/new-analysis";
import {
  AnalysisConfigs,
  AnalysisConfigs as Configs,
} from "@pages/platform-analysis-v2/configs";
import {
  getAnalysisTableColumnDefs,
  GetAnalysisTableColumnDefsProps,
  getPracticeOverviewNavigateRoute,
  AnalysisNameOfFactory as nameOf,
} from "@pages/platform-analysis-v2/utils";
import { RootState } from "@redux/store";
import { TableState } from "@tanstack/react-table";
import { useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { generatePath, useNavigate } from "react-router";
import { AnalysisUpgradeToModifyNotification } from "./components/notifications/modify-platform-analysis";

export const AnalysisListView = () => {
  const navigate = useNavigate();
  const { notify, closeNotify } = useNotification();
  const { palette } = useTheme();
  const { user } = useSelector((state: RootState) => state.auth);

  const {
    isSupplierBdmSale,
    isSupplierGroup,
    isAdviserGroup,
    isAdviserTrialGroup,
  } = useUserPermissions();
  const {
    setReachAnalysisSetupPage,
    updatePlatformAnalysisStatus,
    duplicatePlatformAnalysis,
  } = usePlatformAnalysis();

  const ref = useRef<SHDataGridRef | null>(null);

  const [duplicatingIds, setDuplicatingIds] = useState<string[]>([]);
  const [submittingIds, setSubmittingIds] = useState<string[]>([]);
  const [gridState, setGridState] = useState<Partial<TableState>>({
    ...DefaultDataState,
    sorting: [{ id: nameOf("lastModifiedDate"), desc: true }],
    columnFilters: [
      {
        id: nameOf("status"),
        value: [AnalysisStatus.InProgress, AnalysisStatus.Completed],
      },
    ],
  });

  // Functions
  const trialPermissionDeniedNotify = (
    actionType: "create" | "edit" | "duplicate",
  ) => {
    notify(
      <AnalysisUpgradeToModifyNotification
        actionType={actionType}
        onNavigate={() => {
          navigate(
            getPracticeOverviewNavigateRoute(
              user?.userMetadata?.adviser_firm_id,
            ),
          );
          closeNotify(actionType);
        }}
      />,
      {
        ...AnalysisConfigs.DefaultSnackbarOptions,
        key: actionType,
      },
    );
  };

  const handleOnDuplicate = async (analysisId: string) => {
    if (isAdviserTrialGroup) {
      trialPermissionDeniedNotify("duplicate");

      return;
    }

    setDuplicatingIds((ids) => [...ids, analysisId]);
    await duplicatePlatformAnalysis(analysisId);
    setDuplicatingIds((ids) => ids.filter((id) => id !== analysisId));
    ref.current?.refreshOdata();
  };

  const handleOnChangeStatus = async (
    analysisId: string,
    status: AnalysisStatus,
  ) => {
    setSubmittingIds((ids) => [...ids, analysisId]);
    await updatePlatformAnalysisStatus(analysisId, status);
    setSubmittingIds((ids) => ids.filter((id) => id !== analysisId));
    ref.current?.refreshOdata();
  };

  const handleOnEditButtonClick = (analysisId: string) => {
    if (isAdviserTrialGroup) {
      trialPermissionDeniedNotify("edit");

      return;
    }

    setReachAnalysisSetupPage("FIRST_TIME");
    navigate(
      generatePath(PageRoutes.platformAnalysisDetail, {
        analysisId: analysisId,
      }),
      { state: { pageMode: PageMode.Edit } as ParentState },
    );
  };

  const renderItemActions = (itemData: AnalysisListDTO): JSX.Element => {
    return (
      <SHStack direction="row" justifyContent={"space-between"} spacing={0.5}>
        {itemData.status === AnalysisStatus.InProgress ? (
          <SHButton
            sx={{ width: "50%", fontSize: Configs.DefaultFontSize }}
            startIcon={<EditSVG color={palette.common.white} />}
            variant="contained"
            disabled={false}
            onClick={() => handleOnEditButtonClick(itemData.id)}
          >
            Edit
          </SHButton>
        ) : (
          <SHButton
            sx={{ width: "50%" }}
            variant="contained"
            disabled={false}
            onClick={() => {
              navigate(
                generatePath(PageRoutes.platformAnalysisDetail, {
                  analysisId: itemData.id,
                }),
                { state: { pageMode: PageMode.View } as ParentState },
              );
            }}
          >
            View
          </SHButton>
        )}
        <DuplicateButton
          tooltipTitle={"Duplicate"}
          disabled={duplicatingIds.includes(itemData.id)}
          isLoading={duplicatingIds.includes(itemData.id)}
          onClick={async () => {
            await handleOnDuplicate(itemData.id);
          }}
        />
        {itemData.status === AnalysisStatus.Archived ? (
          <RollBackButton
            disabled={submittingIds.includes(itemData.id)}
            isLoading={submittingIds.includes(itemData.id)}
            onClick={async () =>
              await handleOnChangeStatus(itemData.id, AnalysisStatus.Restore)
            }
          />
        ) : (
          <GrayDeleteButton
            tooltipTitle={"Archive"}
            disabled={submittingIds.includes(itemData.id)}
            isLoading={submittingIds.includes(itemData.id)}
            onClick={async () =>
              await handleOnChangeStatus(itemData.id, AnalysisStatus.Archived)
            }
          />
        )}
      </SHStack>
    );
  };

  // States
  const columns = useMemo(() => {
    const userType: GetAnalysisTableColumnDefsProps["userType"] =
      isSupplierGroup ? "supplier" : isAdviserGroup ? "adviser" : undefined;

    return getAnalysisTableColumnDefs({
      userType,
      isSupplierBdmSale,
      renderItemActions,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submittingIds, duplicatingIds, isSupplierBdmSale]);

  return (
    <>
      <TopBarContainer>
        <TopBar
          title={isAdviserGroup ? "Suitability Reviews" : "Platform analysis"}
          tools={<AddNewAnalysisButton />}
        />
      </TopBarContainer>

      <SHBox sx={{ marginTop: "25px", width: "100%" }}>
        <SHDataGrid
          ref={ref}
          odata={{
            url: Configs.DefaultODataRoute,
            pickColumns: isSupplierGroup
              ? Configs.SupplierPickColumns
              : Configs.AdviserPickColumns,
            globalFilterColumns: isSupplierGroup
              ? Configs.SupplierGlobalFilterColumns
              : Configs.AdviserGlobalFilterColumns,
          }}
          emptyMessage={"No platform analysis."}
          columns={columns}
          state={gridState}
          onChangeState={(state) => setGridState(state)}
          searchBoxProps={{
            placeholder: isSupplierGroup
              ? "Search analysis, owners and advisers"
              : "Search advisers, family groups and reviews",
            width: isSupplierGroup ? undefined : "355px",
          }}
        />
      </SHBox>
    </>
  );
};
